import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Smartphone, Check, Sparkles, Flame, Loader2 } from "lucide-react";
import { isInstallAvailable, promptInstall } from "../lib/pwa";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const SATAppPage = () => {
  const { user } = useAuth();
  const [installable, setInstallable] = useState(isInstallAvailable());
  const [installed, setInstalled] = useState(false);
  const [impulse, setImpulse] = useState(null);
  const [streak, setStreak] = useState(null);
  const [doneToday, setDoneToday] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onAvail = () => setInstallable(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("sat-install-available", onAvail);
    window.addEventListener("appinstalled", onInstalled);
    api.get("/impulses/today").then(r => setImpulse(r.data));
    return () => {
      window.removeEventListener("sat-install-available", onAvail);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (user) api.get("/impulses/streak").then(r => setStreak(r.data));
  }, [user]);

  const install = async () => {
    const r = await promptInstall();
    if (r.outcome === "accepted") setInstalled(true);
  };

  const completeToday = async () => {
    if (!user) { window.location.href = "/auth/login?redirect=/satapp"; return; }
    setBusy(true);
    try {
      await api.post("/impulses/complete", { impulse_id: impulse.id });
      setDoneToday(true);
      const r = await api.get("/impulses/streak");
      setStreak(r.data);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="satapp-page">
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=2400&q=85" alt="" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#081328]/40 via-[#081328]/70 to-[#081328]"></div>
        </div>

        <div className="relative container-sat max-w-[1100px]">
          <Link to="/ecosystem" className="btn-ghost text-sm">← Ökosystem</Link>
          <div className="overline mt-6 mb-3">SAT · App</div>
          <h1 className="headline-caps text-[10vw] sm:text-7xl text-[#F7F6F2]">
            <span className="block">TRANSFORMATION</span>
            <span className="block headline-italic" style={{ textTransform: "none", fontStyle: "italic", fontWeight: 500 }}>in deiner Tasche.</span>
          </h1>
          <div className="mt-6 flex gap-3">
            {installed ? <span className="btn-secondary cursor-default"><Check size={16} /> Installiert</span>
              : installable ? <button onClick={install} className="btn-primary pulse-gold" data-testid="satapp-install"><Download size={16} /> Installieren</button>
              : <span className="btn-secondary cursor-default"><Smartphone size={16} /> Im Browser-Menü → App installieren</span>}
          </div>
        </div>
      </section>

      {/* Today's impulse — the heart of the app */}
      <section className="py-12 bg-[#050B1B]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="glass rounded-3xl p-8 md:p-12" data-testid="impulse-today">
            <div className="flex items-center justify-between mb-6">
              <div className="overline">Spielregel des Tages</div>
              {streak && streak.streak > 0 && (
                <div className="flex items-center gap-2 text-[#D4AF37]" data-testid="streak-badge">
                  <Flame size={16} /> <span className="text-sm font-semibold">{streak.streak} Tage Streak</span>
                </div>
              )}
            </div>
            {!impulse ? <Loader2 className="animate-spin mx-auto text-white/30" /> : (
              <>
                <div className="text-xs uppercase tracking-widest text-[#D4AF37]/80 mb-3">{impulse.rule}</div>
                <h2 className="serif text-4xl md:text-5xl font-light">{impulse.title}</h2>
                <p className="text-white/80 text-lg leading-relaxed mt-6">{impulse.body}</p>
                <div className="mt-6 border-l-2 border-[#D4AF37] pl-5">
                  <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">Übung</div>
                  <p className="text-white/75 italic">{impulse.practice}</p>
                </div>
                <div className="mt-8 flex gap-3">
                  {doneToday ? (
                    <span className="btn-secondary cursor-default"><Check size={16} className="text-[#D4AF37]" /> Heute erledigt</span>
                  ) : (
                    <button onClick={completeToday} disabled={busy} className="btn-primary" data-testid="impulse-complete">
                      {busy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Heute erledigt
                    </button>
                  )}
                  <Link to="/ai" className="btn-secondary">Darüber mit SAT AI sprechen <ArrowRight size={14} /></Link>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#081328]">
        <div className="container-sat max-w-[1100px] grid md:grid-cols-3 gap-6">
          {[
            { t: "Tägliche Impulse", d: "Eine SAT-Spielregel pro Tag — bewusst gewählt für deinen Weg." },
            { t: "Offline lernen", d: "Lektionen sind auch ohne Internet verfügbar." },
            { t: "Schnell wie nativ", d: "Service-Worker-basiert, sub-sekunden-schneller Start." },
          ].map((b, i) => (
            <div key={i} className="glass rounded-2xl p-8"><Sparkles size={18} className="text-[#D4AF37] mb-3" /><div className="serif text-2xl">{b.t}</div><p className="text-white/65 mt-2">{b.d}</p></div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SATAppPage;
